import { router } from "@inertiajs/react";
import {
  Settings,
  AlertCircle,
  Info,
  Loader2,
} from "lucide-react";
import Layout from "../Layout";
import { BreadcrumbNavigation } from "../../components/BreadcrumbNavigation";
import { PageHeader } from "../../components/PageHeader";
import { InfoAlert } from "../../components/ui/InfoAlert";
import { RecommendationSummaryBar } from "../../components/recommendations/RecommendationSummaryBar";
import { RecommendationTable } from "../../components/recommendations/RecommendationTable";
import { RecommendationSummary, RecommendationSection } from "@/types/recommendation";

interface Workspace {
  name: string;
  slug: string;
}

interface ScanStatus {
  scan_type: "metrics" | "analysis";
  status: "in_progress" | "completed";
  progress_percentage: number;
  records_total: number;
  records_completed: number;
}

interface AwsAccountShowProps {
  uuid: string;
  display_name: string;
  account_id: string;
  role_arn: string;
  status: "untested" | "connected" | "error";
  last_tested_at: string | null;
  last_error: string | null;
  workspace: Workspace;
  recommendation_summary: RecommendationSummary;
  recommendation_sections: RecommendationSection[];
  scan_status: ScanStatus | null;
  has_terraform_states: boolean;
  can_edit: boolean;
  can_delete: boolean;
  organization_prs_remaining: number;
  organization_plan: string;
}

export default function AwsAccountShow({
  uuid,
  display_name,
  status,
  last_error,
  workspace,
  recommendation_summary,
  recommendation_sections,
  scan_status,
  has_terraform_states,
  can_edit,
  organization_prs_remaining,
  organization_plan,
}: AwsAccountShowProps) {
  const breadcrumbs = [
    { label: "Workspaces", href: "/workspaces" },
    { label: workspace.name, href: `/workspaces/${workspace.slug}` },
    { label: display_name },
  ];

  const handleSettingsClick = () => {
    router.visit(`/workspaces/${workspace.slug}/aws_accounts/${uuid}/edit`);
  };

  const hasAnyRecommendations = recommendation_summary.total_count > 0;

  return (
    <Layout title={display_name}>
      <section className="px-4 sm:px-6 lg:px-8 py-16 pt-40">
        <div className="max-w-4xl mx-auto">
          <BreadcrumbNavigation items={breadcrumbs} />

          <PageHeader
            title={display_name}
            actions={
              can_edit && (
                <button
                  onClick={handleSettingsClick}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-zinc-800 text-gray-700 dark:text-gray-200 text-xs font-medium rounded-full border border-gray-300 dark:border-zinc-700 hover:bg-gray-50 dark:hover:bg-zinc-700 transition-colors"
                >
                  <Settings className="w-4 h-4" />
                  Settings
                </button>
              )
            }
          />

          {/* Error Alert */}
          {last_error && status === "error" && (
            <div className="mt-6 p-3 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900 rounded-xl flex items-start gap-2.5">
              <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-xs font-semibold text-red-900 dark:text-red-100 mb-0.5">
                  Connection Error
                </h3>
                <p className="text-xs text-red-800 dark:text-red-200">{last_error}</p>
              </div>
            </div>
          )}

          {/* Scan in progress */}
          {scan_status && (
            <div className="mt-6 flex flex-col items-center justify-center py-6 rounded-2xl border bg-background/80 shadow-xl">
              <Loader2 className="w-8 h-8 text-primary animate-spin mb-2" />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-0.5">
                {scan_status.scan_type === "metrics"
                  ? "Collecting metrics..."
                  : "Analyzing..."}
              </h3>
              <p className="text-xs text-gray-600 dark:text-gray-400 text-center max-w-md mb-1.5">
                {scan_status.scan_type === "metrics"
                  ? "We're collecting CloudWatch metrics for your AWS account."
                  : "We're analyzing your resources for optimization opportunities."}
              </p>
              {scan_status.records_total > 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {scan_status.scan_type === "metrics" ? "Collecting" : "Analyzing"}{" "}
                  {scan_status.records_completed} of {scan_status.records_total} resources (
                  {scan_status.progress_percentage}%)
                </p>
              )}
            </div>
          )}

          {!scan_status && (
            <>
              {/* Terraform info alert */}
              {hasAnyRecommendations && !has_terraform_states && (
                <InfoAlert icon={Info} variant="info" className="mt-6">
                  Connect a Terraform state in{" "}
                  <a
                    href={`/workspaces/${workspace.slug}/aws_accounts/${uuid}/edit#infrastructure-states`}
                    className="font-medium underline hover:text-blue-900 dark:hover:text-blue-200"
                  >
                    Settings
                  </a>{" "}
                  to enable pull request generation for these recommendations.
                </InfoAlert>
              )}

              {/* Summary bar */}
              <div className="mt-6">
                <RecommendationSummaryBar summary={recommendation_summary} />
              </div>

              {/* Recommendation tables */}
              <div className="mt-4 space-y-4">
                {recommendation_sections.map((section) => (
                  <RecommendationTable
                    key={section.key}
                    section={section}
                    workspaceSlug={workspace.slug}
                    organizationPrsRemaining={organization_prs_remaining}
                    organizationPlan={organization_plan}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </Layout>
  );
}
